// --- Multi-hoja por sitio & hoja única por sitio ---
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ESTATUS } from '../../config/config';
import { VEHICULOS_SCHEMA } from '../../data/vehiculosColumns.schema';
import { getExportTimestamp } from '../formatters/dates';

// -------------------------------
// Configuración de auto-ancho
// -------------------------------
const COL_MARGIN = 5;  // Margen extra para que no quede muy justo

// -------------------------------
// Utils
// -------------------------------
// Bordes grisecitos para celdas de tabla
const THIN_BORDER = {
  top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
  left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
  bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
  right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
};
// Colores para estatus
const getStatusExcelStyle = (estatus) => {
  // Inventario (1) → azul pastel
  if (estatus === ESTATUS.INVENTARIO) {
    return {
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F2FE' } }, // azul muy suave
      font: { color: { argb: 'FF1D4ED8' }, bold: true }, // azul 700
      align: { horizontal: 'center' },
    };
  }
  // Facturado no entregado (6) → morado pastel
  if (estatus === ESTATUS.FACTURADO_NO_ENTREGADO) {
    return {
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDE9FE' } }, // morado suave
      font: { color: { argb: 'FF6D28D9' }, bold: true }, // violet 700
      align: { horizontal: 'center' },
    };
  }
  return null;
};


const sanitizeSheetName = (name, used = new Set()) => {
  const base = (name || 'Hoja').replace(/[\\/*?:[\]]/g, '').slice(0, 31) || 'Hoja';
  let final = base, i = 1;
  while (used.has(final)) {
    const suf = ` (${++i})`;
    final = `${base.slice(0, 31 - suf.length)}${suf}`;
  }
  used.add(final);
  return final;
};

const asDashIfEmpty = (v) => (v === null || v === undefined || v === '' ? '-' : v);
const asDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'string') {
    const ddmm = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(v);
    if (ddmm) {
      const d = new Date(parseInt(ddmm[3], 10), parseInt(ddmm[2], 10) - 1, parseInt(ddmm[1], 10));
      return isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const toExcelDateInUserTZ = (raw) => {
  const d = asDate(raw);
  if (!d) return null;
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000);
};

// const tsString = () => ... REMOVED in favor of getExportTimestamp

const ensureVisibleColumns = (visibleColumns) =>
  Array.isArray(visibleColumns) && visibleColumns.length
    ? visibleColumns
    : Object.keys(VEHICULOS_SCHEMA);

// -------------------------------
// Sheet writer (reutilizable)
// -------------------------------
/**
 * Pinta una hoja con:
 *  - Título arriba (Estado / Sitio)
 *  - Encabezado de columnas
 *  - Bloques por subsitio (cada bloque con subtítulo + conteo y sus filas)
 *
 * @param {Worksheet} ws
 * @param {{
 *   title: string,
 *   sections: Array<{nombre:string, vehiculos:Array<object>}>,
 *   visibleColumns: string[],
 *   schema: object,
 *   sectionTitleFormatter?: (sec)=>string
 * }} cfg
 */
function writeSheetWithSections(ws, {
  title,
  sections,
  visibleColumns,
  schema,
  sectionTitleFormatter = (sec) => `${sec.nombre} — ${sec.vehiculos?.length ?? 0} vehículos`,
}) {
  // Columnas (solo para que ExcelJS sepa la estructura)
  const headers = visibleColumns.map((key) => ({
    key,
    width: 10,
  }));
  ws.columns = headers;

  // --- TÍTULO DEL SITIO (fila 1) ---
  ws.mergeCells(1, 1, 1, headers.length);
  const tcell = ws.getCell(1, 1);
  tcell.value = title || 'Vehículos';
  tcell.font = { bold: true, size: 15 };
  tcell.alignment = { horizontal: 'left', vertical: 'middle' };
  tcell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } };
  ws.getRow(1).height = 22;

  // NO congelar encabezados
  ws.views = [];

  let isFirstSection = true; // 🎯 Flag para detectar la primera sección

  // --- SECCIONES POR SUBSITIO ---
  for (const sec of sections) {
    if (!sec?.vehiculos?.length) continue;

    // 🎯 DOS líneas de separación antes de CADA subsitio
    ws.addRow([]);
    ws.addRow([]);

    // 1) Fila de título de sección: "Subsitio X — N vehículos"
    const secTitleRow = ws.addRow([]);
    ws.mergeCells(secTitleRow.number, 1, secTitleRow.number, headers.length);
    const scell = ws.getCell(secTitleRow.number, 1);
    scell.value = sectionTitleFormatter(sec);
    scell.font = { bold: true };
    scell.alignment = { horizontal: 'left', vertical: 'middle' };
    scell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDE9FE' } };

    // 🎯 2) HEADER de la mini-tabla
    const miniHeaderRow = ws.addRow([]);
    visibleColumns.forEach((key, idx) => {
      const col = schema[key] || {};
      const cell = miniHeaderRow.getCell(idx + 1);
      cell.value = col.header ?? key;
      cell.font = { bold: true };
      cell.alignment = { vertical: 'middle' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
      cell.border = THIN_BORDER;
    });

    // 🎯 AUTOFILTER solo para el PRIMER subsitio
    if (isFirstSection) {
      ws.autoFilter = {
        from: { row: miniHeaderRow.number, column: 1 },
        to: { row: miniHeaderRow.number, column: headers.length },
      };
      isFirstSection = false;
    }

    // 3) Filas de datos de la sección
    for (const rowData of sec.vehiculos) {
      const rowValues = {};
      visibleColumns.forEach((key) => {
        const col = schema[key] || {};
        const raw = typeof col.value === 'function' ? col.value(rowData) : rowData[key];

        if (col.type === 'boolean') {
          rowValues[key] = raw === true ? 'Sí' : raw === false ? 'No' : '-';
        } else if (col.type === 'date' || col.type === 'datetime') {
          const d = (key === 'last_read')
            ? toExcelDateInUserTZ(raw)
            : asDate(raw);

          rowValues[key] = d || '-';
        }
        else {
          rowValues[key] = asDashIfEmpty(raw);
        }
      });

      const added = ws.addRow(rowValues);
      const rowStyle = getStatusExcelStyle(rowData?.estatus);

      visibleColumns.forEach((key, idx) => {
        const col = schema[key] || {};
        const cell = added.getCell(idx + 1);

        // Booleanos "Sí/No"
        if (col.type === 'boolean') {
          if (cell.value === 'Sí') {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F4EA' } };
            cell.font = { color: { argb: 'FF166534' }, bold: true };
            cell.alignment = { horizontal: 'center' };
          } else if (cell.value === 'No') {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
            cell.font = { color: { argb: 'FF991B1B' }, bold: true };
            cell.alignment = { horizontal: 'center' };
          } else {
            cell.alignment = { horizontal: 'center' };
          }
        }

        // Formato de fechas
        if (col.type === 'date') cell.numFmt = 'dd/mm/yyyy';
        if (col.type === 'datetime') cell.numFmt = 'dd/mm/yyyy hh:mm';

        // Color por estatus
        if (rowStyle && key !== 'detectado_rfid') {
          cell.fill = rowStyle.fill;
        }
        if (rowStyle && key === 'estatus') {
          if (rowStyle.font) cell.font = { ...(cell.font || {}), ...rowStyle.font };
          if (rowStyle.align) cell.alignment = { ...(cell.alignment || {}), ...rowStyle.align };
        }

        // Bordecito gris
        cell.border = THIN_BORDER;
      });
    }
  }

  // Auto-ancho de columnas
  ws.columns.forEach((col) => {
    let max = 10;

    col.eachCell({ includeEmpty: false }, (cell) => {
      if (cell.row === 1) return; // Saltar el título
      if (cell.isMerged) return;

      const v = cell.value ?? '';
      const str = v instanceof Date
        ? (cell.numFmt && cell.numFmt.includes('h') ? '00/00/0000 00:00' : '00/00/0000')
        : String(v);

      max = Math.max(max, str.length);
    });

    col.width = max + COL_MARGIN;
  });
}


// -------------------------------
// Export público 1: un solo sitio -> 1 hoja con subsitios
// -------------------------------
/**
 * Exporta un solo sitio (una hoja) con secciones por subsitio.
 * @param {Array<{nombre:string, vehiculos:Array<object>}>} secciones
 * @param {string[]} visibleColumns
 * @param {{estado?:string, sitio?:string}} meta
 */
export async function exportSitioConSubsitiosEnUnaHoja(secciones, visibleColumns, meta = {}) {
  const cols = ensureVisibleColumns(visibleColumns);
  const wb = new ExcelJS.Workbook();
  const usedNames = new Set();
  const sheetName = sanitizeSheetName(meta.sitio || 'Sitio', usedNames);
  const ws = wb.addWorksheet(sheetName);

  const title = meta.estado && meta.sitio ? `${meta.estado} / ${meta.sitio}` : (meta.sitio || 'Sitio');

  writeSheetWithSections(ws, {
    title,
    sections: secciones,
    visibleColumns: cols,
    schema: VEHICULOS_SCHEMA,
  });

  const estadoSlug = (meta.estado || 'estado').replace(/\s+/g, '_');
  const sitioSlug = (meta.sitio || 'sitio').replace(/\s+/g, '_');
  const filename = `vehiculos_${estadoSlug}_${sitioSlug}_${getExportTimestamp()}.xlsx`;

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
}

// -------------------------------
// Export público 2: todo el estado -> N hojas (una por sitio)
// -------------------------------
/**
 * Crea un workbook con N hojas: una por sitio; dentro de cada hoja,
 * secciones por subsitio (con conteo).
 * @param {Array<{sitio:string, secciones:Array<{nombre:string, vehiculos:Array}>}>} sitiosConSecciones
 * @param {string[]} visibleColumns
 * @param {{estado?:string}} meta  
 */
export async function exportEstadoEnHojasPorSitio(sitiosConSecciones, visibleColumns, meta = {}) {
  const cols = ensureVisibleColumns(visibleColumns);
  const wb = new ExcelJS.Workbook();
  const usedNames = new Set();

  for (const { sitio, secciones } of sitiosConSecciones) {
    if (!secciones?.length) continue;

    const sheetName = sanitizeSheetName(sitio, usedNames);
    const ws = wb.addWorksheet(sheetName);

    const title = meta.estado ? `${meta.estado} / ${sitio}` : sitio;

    writeSheetWithSections(ws, {
      title,
      sections: secciones,
      visibleColumns: cols,
      schema: VEHICULOS_SCHEMA,
      // Si prefieres que muestre "Subsitio: X — N vehículos", cambia el formatter:
      // sectionTitleFormatter: (sec) => `Subsitio: ${sec.nombre} — ${sec.vehiculos?.length ?? 0} vehículos`,
    });
  }

  // Nombre del archivo
  const estadoSlug = (meta.estado || 'estado').replace(/\s+/g, '_');
  const filename = `vehiculos_${estadoSlug}_por_sitio_${getExportTimestamp()}.xlsx`;

  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename
  );
}